import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { getEvidenceById } from '../lib/evidence';
import { FANOUT_PAGES } from '../lib/site';

const page = FANOUT_PAGES.find((p) => p.path === '/adaptacoes-sensoriais');
const visual = getEvidenceById('jefferis-2012');
const hearing = getEvidenceById('lim-loo-2018');
const blind = getEvidenceById('ophthalmic-mmse-blind-2010');

const ANSWER =
  'Déficits visuais, auditivos ou motores podem reduzir o escore do MEEM porque parte do teste depende de enxergar, ouvir, escrever ou desenhar. O primeiro passo é corrigir barreiras corrigíveis sem mudar a tarefa. Quando uma limitação impede a execução de um item, modificar ou excluir esse item altera a interpretação do escore; o resultado não deve ser comparado automaticamente aos pontos de corte do MEEM padrão.';

const REFERENCES = [
  `${visual.citation} DOI: ${visual.primarySource.value}.`,
  `${hearing.citation} DOI: ${hearing.primarySource.value}.`,
  `${blind.citation} DOI: ${blind.primarySource.value}.`
];

const RELATED = [
  { to: '/como-aplicar', label: 'Como aplicar o Mini Mental', note: 'padronização e condições gerais da aplicação.' },
  { to: '/erros-de-aplicacao', label: 'Erros comuns de aplicação', note: 'o que compromete comparabilidade e validade.' },
  { to: '/interpretacao-dos-resultados', label: 'Como interpretar o resultado', note: 'leia o escore junto do contexto clínico e funcional.' },
  { to: '/pontos-de-corte', label: 'Pontos de corte do Mini Mental', note: 'entenda por que um total modificado não herda automaticamente os mesmos limiares.' },
  { to: '/mini-mental-ou-moca', label: 'Mini Mental ou MoCA', note: 'compare limites e usos de instrumentos de rastreio.' }
];

function InlineLink({ to, children }) {
  return <Link to={to} className=" text-[var(--primary)] hover:underline">{children}</Link>;
}

export default function AdaptacoesSensoriais() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="via-teste">
        <h2 id="via-teste" className="text-2xl mb-5">A limitação é cognitiva ou é da via usada pelo teste?</h2>
        <p className="text-[var(--text-muted)]">
          Essa é a primeira pergunta. Um erro pode refletir a habilidade cognitiva investigada, mas também pode ocorrer porque a pessoa não conseguiu enxergar, ouvir ou executar fisicamente a tarefa. Em alguns itens, esses componentes se misturam; por isso, nem sempre é possível separar perfeitamente a causa apenas pelo escore.
        </p>
      </section>

      <section aria-labelledby="corrigir-barreiras">
        <h2 id="corrigir-barreiras" className="text-2xl mb-5">O que pode ser corrigido antes da aplicação?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Acomodar o ambiente é diferente de modificar o instrumento. Sempre que fizer parte da rotina da pessoa, favoreça o uso de óculos e aparelho auditivo habituais, iluminação adequada, menos ruído e interrupções e posicionamento que facilite visão e audição.
        </p>
        <p className="text-[var(--text-muted)]">
          Essas medidas tentam remover uma barreira externa sem mudar a tarefa. Não existe, porém, um sistema validado de “pontos compensatórios” para somar ao resultado quando uma limitação persiste.
        </p>
      </section>

      <section aria-labelledby="deficit-visual">
        <h2 id="deficit-visual" className="text-2xl mb-5">Como o déficit visual pode afetar o MEEM?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          No Newcastle 85+ Study, {visual.visualImpairmentSampleSize} dos {visual.sampleSize} participantes tinham deficiência visual registrada. A mediana do sMMSE foi {visual.results.visualImpairmentMedian} nesse grupo e {visual.results.noVisualImpairmentMedian} entre os participantes sem deficiência visual, com pior desempenho especialmente em tarefas dependentes de visão.
        </p>
        <p className="text-[var(--text-muted)]">
          Isso não cria uma correção matemática para baixa visão. Se um item visual não puder ser acessado, sua resposta pode deixar de representar adequadamente a habilidade cognitiva pretendida nas condições padrão. Retirar itens muda o total e exige outra interpretação.
        </p>
      </section>

      <section aria-labelledby="deficit-auditivo">
        <h2 id="deficit-auditivo" className="text-2xl mb-5">Como o déficit auditivo pode afetar o MEEM?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Lim e Loo avaliaram {hearing.sampleSize} participantes de {hearing.ageRange[0]} a {hearing.ageRange[1]} anos e encontraram associação entre pior audição e escores menores. Componentes particularmente dependentes de informação auditiva também tiveram desempenho inferior entre participantes com perda auditiva.
        </p>
        <p className="text-[var(--text-muted)]">
          Uma instrução não ouvida não deve ser tratada automaticamente como evidência de déficit cognitivo. Ao mesmo tempo, perda auditiva e comprometimento cognitivo podem coexistir; a audição explica uma fonte de viés possível, não toda diferença de desempenho.
        </p>
      </section>

      <section aria-labelledby="limitacoes-motoras">
        <h2 id="limitacoes-motoras" className="text-2xl mb-5">E quando a limitação é motora?</h2>
        <p className="text-[var(--text-muted)]">
          Tremor, paresia, artrite e outras limitações podem impedir escrita ou desenho. Nessa situação, registrar a limitação é necessário, mas não torna automaticamente o item válido. Quando a via motora exigida pela tarefa não está disponível, a execução pode se tornar não interpretável segundo o padrão, e qualquer modificação precisa ser documentada.
        </p>
      </section>

      <section aria-labelledby="acomodar-modificar">
        <h2 id="acomodar-modificar" className="text-2xl mb-5">Acomodar não é o mesmo que modificar</h2>
        <div className="space-y-3 text-[var(--text-muted)]">
          <p><strong>Corrigir ambiente ou barreira:</strong> preserva melhor a aplicação padrão quando o conteúdo e a modalidade da tarefa permanecem os mesmos.</p>
          <p><strong>Mudar estímulo, modalidade ou tarefa:</strong> produz uma aplicação modificada e pode alterar o que está sendo medido.</p>
          <p><strong>Excluir itens:</strong> muda o denominador; o total resultante não deve ser comparado automaticamente com pontos de corte definidos para o MEEM padrão.</p>
        </div>
        <p className="text-[var(--text-muted)] mt-5">
          Para entender por que isso altera a leitura do número, veja <InlineLink to="/pontos-de-corte">como os pontos de corte dependem do instrumento e da população estudada</InlineLink>.
        </p>
      </section>

      <section aria-labelledby="mmse-blind">
        <h2 id="mmse-blind" className="text-2xl mb-5">E o MMSE-blind ou outras versões modificadas?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Versões modificadas foram estudadas para populações específicas e usam escalas próprias. Em uma amostra oftalmológica com {blind.sampleSize} participantes, a forma MMSE-blind utilizada tinha máximo de {blind.results.mmseBlindMax} pontos, diferente do máximo de {blind.results.mmseStandardMax} do teste padrão.
        </p>
        <p className="text-[var(--text-muted)]">
          O exemplo mostra por que retirar itens não cria simplesmente “o mesmo MEEM com menos perguntas”. Uma versão modificada precisa ser identificada como tal e interpretada de acordo com a evidência própria; não se deve recalcular proporcionalmente o total para imitar a escala padrão.
        </p>
      </section>

      <section aria-labelledby="registro">
        <h2 id="registro" className="text-2xl mb-5">Como registrar uma aplicação comprometida por barreira sensorial ou motora?</h2>
        <ol className="space-y-3 text-[var(--text-muted)] list-decimal pl-5">
          <li>registre a limitação visual, auditiva ou motora observada;</li>
          <li>registre o recurso habitual usado para corrigi-la;</li>
          <li>identifique quais partes da avaliação podem ter sido afetadas;</li>
          <li>documente qualquer mudança de modalidade ou tarefa;</li>
          <li>registre se o total ainda é comparável à aplicação padrão;</li>
          <li>explicite a limitação de interpretabilidade no resultado.</li>
        </ol>
      </section>

      <section aria-labelledby="fluxo-final">
        <h2 id="fluxo-final" className="text-2xl mb-5">Fluxo prático</h2>
        <div className="p-5 border border-[var(--border)] rounded-2xl bg-[var(--surface-soft)] font-bold leading-relaxed">
          identificar limitação → corrigir o corrigível → aplicar sem alterar quando possível → documentar itens comprometidos → evitar comparação inválida → escolher instrumento ou versão apropriada quando necessário
        </div>
        <p className="text-[var(--text-muted)] mt-5">
          Depois, interprete o resultado dentro do contexto clínico em <InlineLink to="/interpretacao-dos-resultados">como interpretar o resultado do Mini Mental</InlineLink>. Para padronização da avaliação, consulte também <InlineLink to="/como-aplicar">como aplicar o Mini Mental</InlineLink> e <InlineLink to="/erros-de-aplicacao">erros comuns de aplicação</InlineLink>.
        </p>
      </section>
    </ArticleLayout>
  );
}
